import React, { useEffect, useState } from 'react';
import { Flex } from 'rebass';
import PropTypes from 'prop-types';

import AddressPicker, { AddressType } from '../Address/AddressPicker';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Label, Select } from '../Form';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';

type MoveAssemblyModalProps = {
  id: string;
  onClose: () => void;
};

const FETCH_SERVICES = gql(`
  query WaterServices($where: JSON) {
    waterServices(where: $where, orderBy: [{ col: "siteNumber", dir: DESC }]) {
      id
      siteNumber
      meterNumber
    }
  }
`);

const SET_WATER_SERVICE = gql(`
  mutation SetWaterService($assemblyIds: [ID!]!, $waterServiceId: ID!) {
    setAssembliesWaterService(
      assemblyIds: $assemblyIds
      waterServiceId: $waterServiceId
    ) {
      id
      addressId
      address {
        id
        street1
        street2
        city
        state
        zip
      }
      waterServiceId
      waterService {
        id
        siteNumber
        meterNumber
        pin
      }
    }
  }
`);

const MoveAssemblyModal = ({ id, onClose }: MoveAssemblyModalProps) => {
  const [addressId, setAddressId] = useState<string>();
  const [waterServiceId, setWaterServiceId] = useState<string>();

  const updateAddress = (newAddress: AddressType | null) => {
    setAddressId(newAddress?.id);
  };

  const { data } = useQuery<{
    waterServices: { id: string; siteNumber: string; meterNumber: string }[];
  }>(FETCH_SERVICES, {
    variables: {
      where: {
        addressId,
      },
    },
    skip: !addressId,
  });

  useEffect(() => {
    if (data?.waterServices?.[0]) {
      setWaterServiceId(data.waterServices[0].id);
    }
  }, [data?.waterServices]);

  const [mutate, mutationResult] = useMutation(SET_WATER_SERVICE);

  const save = () => {
    mutate({
      variables: {
        assemblyIds: [id],
        waterServiceId: waterServiceId!,
      },
      onCompleted: onClose,
    });
  };

  return (
    <Modal title="Move Assembly" onClose={onClose}>
      <ModalBody>
        <Flex flexDirection="column" m={3}>
          <Label>New Address</Label>
          <AddressPicker
            addressId={addressId}
            allowCreate={false}
            placeholder="Enter address..."
            onChange={(newAddress) => updateAddress(newAddress)}
          />
        </Flex>
        {data?.waterServices &&
          (data?.waterServices.length > 0 ? (
            <Flex flexDirection="column" m={3}>
              <Label>Water Service</Label>
              <Select
                value={waterServiceId}
                onChange={(e) => {
                  setWaterServiceId(e.target.value);
                }}
              >
                {data?.waterServices.map((waterService) => (
                  <option key={waterService.id} value={waterService.id}>
                    {waterService.siteNumber}
                  </option>
                ))}
              </Select>
            </Flex>
          ) : (
            'This address has no water services'
          ))}
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save} disabled={!waterServiceId}>
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

MoveAssemblyModal.propTypes = {
  onClose: PropTypes.any,
};

export default MoveAssemblyModal;
